import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api-error";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StableInput, StableTextarea } from "@/components/StableField";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableCombobox } from "@/components/SearchableCombobox";
import {
  arCreateCategory,
  arCreateClient,
  arCreateReceivable,
  arGetReceivable,
  arListCategories,
  arListClients,
  arUpdateReceivable,
} from "@/lib/ar-api";
import { listPaymentMethods } from "@/lib/api";
import { arRecurrenceIT, arRecurrenceOptions } from "@/lib/ar-format";

/**
 * ReceivableForm — dual-mode form (recurring + ordinary).
 *
 * Mode is detected via URL pathname (/receivables/ordinary/* vs everything
 * else). In ordinary mode, recurrence is forced to one_time, end_date is
 * null, and the recurrence configuration UI is hidden. issue_day_offset
 * controls how many days BEFORE the due date the invoice is issued (e.g.,
 * for fees billed 30 days in advance) — tunable in both modes.
 */
export default function ReceivableForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sourceId = searchParams.get("from");

  const isOrdinary = location.pathname.startsWith("/receivables/ordinary");
  const listPath = isOrdinary ? "/receivables/ordinary" : "/receivables/recurring";

  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  // Gate: in edit/duplicate mode we must NOT render the inputs until the
  // async fetch lands, otherwise the late setForm({...fetched}) call would
  // overwrite anything the user has already typed.
  const [loaded, setLoaded] = useState(!editing && !sourceId);

  const [form, setForm] = useState({
    title: "",
    description: "",
    client_id: "",
    category_id: "",
    planned_amount: "",
    currency: "EUR",
    recurrence_type: isOrdinary ? "one_time" : "monthly",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    day_of_month: "",
    issue_day_offset: 0,
    notes: "",
    // Iter41 — dati fattura (solo per ordinari). Per i ricorrenti i campi
    // vengono inseriti al momento della Collection.
    invoice_reference: "",
    invoiced_at: "",
    default_payment_method_id: "",
  });

  useEffect(() => {
    (async () => {
      const [cl, ca, pm] = await Promise.all([
        arListClients(),
        arListCategories(),
        listPaymentMethods().catch(() => []),
      ]);
      setClients(cl || []);
      setCategories(ca || []);
      setPaymentMethods(pm || []);
      if (editing) {
        try {
          const r = await arGetReceivable(id);
          setForm({
            title: r.title || "",
            description: r.description || "",
            client_id: r.client_id || "",
            category_id: r.category_id || "",
            planned_amount: String(r.planned_amount ?? ""),
            currency: r.currency || "EUR",
            recurrence_type: r.recurrence_type || "monthly",
            start_date: r.start_date || "",
            end_date: r.end_date || "",
            day_of_month: r.day_of_month || "",
            issue_day_offset: r.issue_day_offset ?? 0,
            notes: r.notes || "",
            // Iter41: precarica dati fattura (popolati dal backend per ordinari)
            invoice_reference: r.invoice_reference || "",
            invoiced_at: r.invoiced_at || "",
            default_payment_method_id: r.default_payment_method_id || "",
          });
        } catch {
          toast.error("Impossibile caricare l'incasso");
        } finally {
          setLoaded(true);
        }
      } else if (sourceId) {
        try {
          const src = await arGetReceivable(sourceId);
          setForm({
            title: src.title ? `${src.title} (copia)` : "",
            description: src.description || "",
            client_id: src.client_id || "",
            category_id: src.category_id || "",
            planned_amount: String(src.planned_amount ?? ""),
            currency: src.currency || "EUR",
            recurrence_type: isOrdinary ? "one_time" : src.recurrence_type || "monthly",
            start_date: new Date().toISOString().slice(0, 10),
            end_date: "",
            day_of_month: src.day_of_month || "",
            issue_day_offset: src.issue_day_offset ?? 0,
            notes: src.notes || "",
            default_payment_method_id: src.default_payment_method_id || "",
          });
          toast.success("Dati duplicati: rivedi e salva.");
        } catch {
          toast.error("Errore duplicazione");
        } finally {
          setLoaded(true);
        }
      }
    })();
  }, [id, editing, sourceId, isOrdinary]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.title.trim()) return "Oggetto obbligatorio";
    if (!form.client_id) return "Cliente obbligatorio";
    if (!form.category_id) return "Categoria obbligatoria";
    if (!form.planned_amount || Number(form.planned_amount) < 0)
      return "Importo obbligatorio e non negativo";
    if (!form.start_date)
      return isOrdinary ? "Data scadenza obbligatoria" : "Data di decorrenza obbligatoria";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    const payload = {
      title: form.title,
      description: form.description || null,
      client_id: form.client_id,
      category_id: form.category_id,
      planned_amount: String(form.planned_amount),
      currency: form.currency,
      recurrence_type: isOrdinary ? "one_time" : form.recurrence_type,
      start_date: form.start_date,
      end_date: isOrdinary ? null : form.end_date || null,
      day_of_month: isOrdinary ? null : form.day_of_month ? Number(form.day_of_month) : null,
      issue_day_offset: Number(form.issue_day_offset || 0),
      notes: form.notes || null,
      // Metodo di pagamento predefinito solo per ricorrenti
      default_payment_method_id: isOrdinary
        ? null
        : form.default_payment_method_id || null,
    };
    // Iter41: dati fattura sono utili solo per gli ordinari (one_time). Per
    // i ricorrenti vengono inseriti al momento dell'incasso.
    if (isOrdinary) {
      payload.invoice_reference = form.invoice_reference.trim() || null;
      payload.invoiced_at = form.invoiced_at || null;
    }
    try {
      if (editing) {
        await arUpdateReceivable(id, payload);
        toast.success(
          isOrdinary
            ? "Incasso ordinario aggiornato"
            : "Serie ricorrente aggiornata: scadenze future non modificate ricalcolate",
        );
      } else {
        await arCreateReceivable(payload);
        toast.success(isOrdinary ? "Incasso ordinario creato" : "Serie ricorrente creata");
      }
      navigate(listPath);
    } catch (e) {
      toast.error(formatApiError(e, "Errore salvataggio"));
    }
  };

  const headerTitle = isOrdinary
    ? editing
      ? "Modifica incasso ordinario"
      : "Nuovo incasso ordinario"
    : editing
      ? "Modifica serie ricorrente"
      : "Nuova serie ricorrente di incassi";

  return (
    <div data-testid={isOrdinary ? "ordinary-receivable-form-page" : "recurring-receivable-form-page"}>
      <PageHeader
        title={headerTitle}
        subtitle={
          isOrdinary
            ? "Singola scadenza incasso (fattura una tantum, prestazione occasionale, rimborso)."
            : editing
              ? "Modifiche valgono per le scadenze future non personalizzate."
              : "Definisci una serie ricorrente. Le scadenze incassi vengono generate automaticamente."
        }
        actions={
          <Button asChild variant="outline" data-testid="btn-back">
            <Link to={listPath}>← Torna all&apos;elenco</Link>
          </Button>
        }
      />

      <div className="rounded-xl border border-stone-200 bg-white p-6">
        {!loaded ? (
          <p className="py-8 text-center text-sm text-stone-500">Caricamento dati…</p>
        ) : (
        <>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <Label>Oggetto</Label>
            <StableInput
              data-testid="input-title"
              placeholder="Es. Compenso dicembre 2025"
              value={form.title}
              onCommit={(v) => setField("title", v)}
            />
          </div>
          <div>
            <Label>Cliente</Label>
            <SearchableCombobox
              value={form.client_id}
              onChange={(v) => setField("client_id", v)}
              onSelectComplete={() => {
                // v1.60 keyboard flow: dopo aver scelto un cliente, focus alla
                // combobox Categoria (via trigger). Usa data-testid già esposto.
                requestAnimationFrame(() => {
                  document
                    .querySelector('[data-testid="input-category-trigger"]')
                    ?.focus();
                });
              }}
              options={clients}
              onCreate={async (name) => {
                try {
                  const created = await arCreateClient({ name });
                  setClients((p) => [...p, created]);
                  toast.success(`Cliente "${name}" creato`);
                  return created;
                } catch {
                  toast.error("Errore creazione cliente");
                  return null;
                }
              }}
              placeholder="Seleziona cliente"
              createLabel="Crea nuovo cliente:"
              emptyLabel="Nessun cliente trovato"
              testId="input-client"
            />
          </div>
          <div>
            <Label>Categoria</Label>
            <SearchableCombobox
              value={form.category_id}
              onChange={(v) => setField("category_id", v)}
              onSelectComplete={() => {
                // Dopo Categoria → focus a input title/description se presente,
                // altrimenti al primo input successivo.
                requestAnimationFrame(() => {
                  document
                    .querySelector('[data-testid="input-title"]')
                    ?.focus();
                });
              }}
              options={categories}
              onCreate={async (name) => {
                try {
                  const created = await arCreateCategory({ name });
                  setCategories((p) => [...p, created]);
                  toast.success(`Categoria "${name}" creata`);
                  return created;
                } catch {
                  toast.error("Errore creazione categoria");
                  return null;
                }
              }}
              placeholder="Seleziona categoria"
              createLabel="Crea nuova categoria:"
              emptyLabel="Nessuna categoria trovata"
              testId="input-category"
            />
          </div>
          {!isOrdinary && (
            <div>
              <Label>Metodo di pagamento (opzionale)</Label>
              <Select
                value={form.default_payment_method_id || "__none__"}
                onValueChange={(v) =>
                  setField(
                    "default_payment_method_id",
                    v === "__none__" ? "" : v,
                  )
                }
              >
                <SelectTrigger data-testid="input-payment-method">
                  <SelectValue placeholder="Non specificato" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Non specificato —</SelectItem>
                  {paymentMethods
                    .filter((m) => m.is_active)
                    .map((m) => (
                      <SelectItem
                        key={m.id}
                        value={m.id}
                        data-testid={`input-payment-method-${m.id}`}
                      >
                        {m.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[10px] text-stone-400">
                Modalità con cui il cliente pagherà (es. SDD, Bonifico).
                Gestisci l&apos;elenco da Impostazioni → Metodi di pagamento.
              </p>
            </div>
          )}
          <div>
            <Label>{isOrdinary ? "Importo previsto (EUR)" : "Importo pianificato (EUR)"}</Label>
            <Input
              type="number"
              step="0.01"
              data-testid="input-amount"
              value={form.planned_amount}
              onChange={(e) => setField("planned_amount", e.target.value)}
            />
          </div>
          <div>
            <Label>{isOrdinary ? "Data scadenza incasso" : "Decorrenza"}</Label>
            <Input
              type="date"
              data-testid="input-start-date"
              value={form.start_date}
              onChange={(e) => setField("start_date", e.target.value)}
            />
          </div>
          {isOrdinary && (
            <>
              <div className="md:col-span-2">
                <div className="rounded-lg border border-stone-200 bg-stone-50/40 p-3">
                  <div className="mb-2 text-[10px] font-medium uppercase tracking-wider text-stone-600">
                    Documento (opzionale)
                  </div>
                  <div className="grid gap-3 md:grid-cols-[2fr_1fr]">
                    <div>
                      <Label className="text-[11px] uppercase tracking-wider text-stone-500">
                        N. Fattura
                      </Label>
                      <Input
                        data-testid="input-invoice-ref"
                        value={form.invoice_reference}
                        onChange={(e) => setField("invoice_reference", e.target.value)}
                        placeholder="Es. Fatt. 119A · Saldo fatt. 12 e 15 · Acconto pratica Rossi"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-wider text-stone-500">
                        Data fattura
                      </Label>
                      <Input
                        type="date"
                        data-testid="input-invoice-date"
                        value={form.invoiced_at}
                        onChange={(e) => setField("invoiced_at", e.target.value)}
                      />
                    </div>
                  </div>
                  <p className="mt-1 text-[10px] leading-snug text-stone-500">
                    Testo libero: puoi indicare più fatture/riferimenti.
                    I dati restano modificabili in seguito.
                  </p>
                </div>
              </div>
            </>
          )}
          {!isOrdinary && (
            <>
              <div>
                <Label>Tipo ricorrenza</Label>
                <Select
                  value={form.recurrence_type}
                  onValueChange={(v) => setField("recurrence_type", v)}
                >
                  <SelectTrigger data-testid="input-recurrence">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {arRecurrenceOptions.filter((r) => r !== "one_time").map((r) => (
                      <SelectItem key={r} value={r}>
                        {arRecurrenceIT[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fine (opzionale)</Label>
                <Input
                  type="date"
                  data-testid="input-end-date"
                  value={form.end_date}
                  onChange={(e) => setField("end_date", e.target.value)}
                />
              </div>
              <div>
                <Label>Giorno del mese (1-31, opzionale)</Label>
                <Input
                  type="number"
                  min="1"
                  max="31"
                  data-testid="input-day-of-month"
                  value={form.day_of_month}
                  onChange={(e) => setField("day_of_month", e.target.value)}
                />
              </div>
            </>
          )}
          <div>
            <Label>Anticipo emissione fattura (giorni prima della scadenza)</Label>
            <Input
              type="number"
              min="0"
              max="366"
              data-testid="input-issue-offset"
              value={form.issue_day_offset}
              onChange={(e) => setField("issue_day_offset", e.target.value)}
            />
            <p className="mt-1 text-[11px] text-stone-500">
              0 = stessa data della scadenza. Es. 30 = la fattura viene emessa 30 giorni prima della data di incasso.
            </p>
          </div>
          <div className="md:col-span-2">
            <Label>Note</Label>
            <StableTextarea
              data-testid="input-notes"
              value={form.notes}
              onCommit={(v) => setField("notes", v)}
            />
          </div>
        </div>

        <div className="mt-6 flex items-center gap-2">
          <Button data-testid="btn-submit" onClick={submit}>
            {isOrdinary
              ? editing
                ? "Salva modifiche"
                : "Crea incasso ordinario"
              : editing
                ? "Salva modifiche serie"
                : "Crea serie ricorrente"}
          </Button>
          <Button asChild variant="outline">
            <Link to={listPath}>Annulla</Link>
          </Button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
